import { isEqual, keyBy, mapValues, union, uniq } from "lodash-es";
import { Action } from "redux";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import { OrganizationSetting } from "@mapmycustomers/shared";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import { Company, EntityType, MapEntity, Person } from "@mapmycustomers/shared/types/entity";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import formatISODate from "@mapmycustomers/shared/util/formatters/formatISODate";

import localSettings from "@app/config/LocalSettings";
import { callApi, callReadonlyApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import {
  getCurrentUser,
  getOrganizationId,
  getOrganizationSettingValue,
  getUserMetaData,
} from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { getMapViewSettings } from "@app/store/map";
import {
  toggleLayerLegendExpandedState,
  toggleLayerVisibility,
} from "@app/store/mapLayers/actions";
import BaseLayerData from "@app/store/mapLayers/BaseLayerData";
import { getUserLocationsData } from "@app/store/mapLayers/selectors";
import { getUsers } from "@app/store/members";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import FaceTime from "@app/types/FaceTime";
import Iam from "@app/types/Iam";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import { METERS_IN_MILE } from "@app/util/consts";
import isUserLocationVisibleToMe from "@app/util/map/isUserLocationVisibleToMe";
import getColorShapeForEntity from "@app/util/map/markerStyles/getColorShapeForEntity";
import { parseApiDateWithTz } from "@app/util/parsers";
import getTimelineEventEntityRef from "@app/util/timeline/getTimelineEventEntityRef";

import {
  colorOrShapeKeyChanged,
  fetchCheckInPins,
  fetchEvents,
  fetchWidgetsData,
  initUserLocations,
  invalidateWidgetsData,
  setDisabledUserIds,
  setSelectedDate,
  setSelectedDatePeriod,
  setSelectedUserIds,
  setShowOtherPins,
} from "./actions";
import UserLocationDatePeriod from "./enums/UserLocationDatePeriod";
import {
  getEvents,
  getEventsDateRange,
  getPendingLegendKeys,
  getSelectedDate,
  getSelectedDatePeriod,
  getSelectedDateRange,
  getSelectedUserIds,
  getSelectedUsers,
  getWidgetsData,
  getWidgetsDataDateRange,
} from "./selectors";
import IsoDateRange from "./types/IsoDateRange";
import UserLocationTimelineEvent from "./types/UserLocationTimelineEvent";
import WidgetsData from "./types/WidgetsData";
import computeInitialSelectedDate from "./utils/computeInitialSelectedDate";
import getWidgetsDataDateRangeForSelection from "./utils/getWidgetsDataDateRangeForSelection";
import toIsoDateRange from "./utils/toIsoDateRange";

function* onFetchEvents({ payload: { dateRange } }: ReturnType<typeof fetchEvents.request>) {
  try {
    const users: User[] = yield select(getSelectedUsers);
    if (users.length === 0) {
      yield put(fetchEvents.success({ events: [] }));
      yield put(fetchCheckInPins.request());
      return;
    }

    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const orgTimeZone: string = yield select(
      getOrganizationSettingValue,
      OrganizationSetting.TIMEZONE
    );

    const [startDate, endDate] = dateRange;

    const timelineResponse: ListResponse<UserLocationTimelineEvent> = yield callReadonlyApi(
      "fetchTimelineEvents",
      organizationId,
      {
        $filters: {
          $and: [
            { "updatedBy.id": { $in: users.map(({ id }) => id) } },
            {
              updatedAt: {
                $gte: startDate,
                $lte: endDate,
                $timezone: orgTimeZone,
              },
            },
            { geoPoint: { $ne: null } },
          ],
        },
        $limit: 10000,
        $order: "-updatedAt",
      }
    );

    yield put(fetchEvents.success({ events: timelineResponse.data }));
    yield put(fetchCheckInPins.request());
  } catch (error) {
    yield put(fetchEvents.failure(error));
    yield put(handleError({ error }));
  }
}

function* onColorOrShapeKeyChanged() {
  yield put(fetchCheckInPins.request());
}

function* onFetchCheckInPins() {
  try {
    const selectedUserIds: User["id"][] = yield select(getSelectedUserIds);
    if (selectedUserIds.length === 0) {
      // Field View is not active — skip the /pins call (applyMapViewSettings fires map-wide) and
      // clear any stale pins.
      yield put(fetchCheckInPins.success({ accountPins: {}, contactPins: {} }));
      return;
    }

    const events: UserLocationTimelineEvent[] = yield select(getEvents);

    const refs = events.map(getTimelineEventEntityRef).filter(isDefined);
    const accountIds = uniq(
      refs.filter((ref) => ref.entityType === EntityType.COMPANY).map((ref) => ref.entityId)
    );
    const contactIds = uniq(
      refs.filter((ref) => ref.entityType === EntityType.PERSON).map((ref) => ref.entityId)
    );

    if (accountIds.length === 0 && contactIds.length === 0) {
      yield put(fetchCheckInPins.success({ accountPins: {}, contactPins: {} }));
      return;
    }

    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const persistedViewState: MapViewState = yield select(getMapViewSettings);
    // getMapViewSettings derives the keys from user metadata, which persists through an API call, so
    // right after a key change the metadata still holds the previous key. The slice keeps the keys
    // the rep switched to, so prefer those; once the metadata lands the two agree.
    const { pendingColorKey, pendingShapeKey }: ReturnType<typeof getPendingLegendKeys> =
      yield select(getPendingLegendKeys);
    const mapViewState: MapViewState = {
      ...persistedViewState,
      colorKey: pendingColorKey ?? persistedViewState.colorKey,
      shapeKey: pendingShapeKey ?? persistedViewState.shapeKey,
    };
    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    // Keyed by entity type, exactly like the regular-map pins request (mapMode/sagas.ts) and
    // EntityAnnotation. EntityType.COMPANY === "accounts", EntityType.PERSON === "contacts",
    // which are exactly the keys the backend's pinLegendApply reads. includeGroups must be set
    // PER-ENTITY (mirroring convertMapFilterToPlatformFilterModel) — a top-level includeGroups is a
    // no-op for the /pins endpoint, so group-based legends would otherwise fall back to a default.
    const entities: Record<string, unknown> = {};
    const pinLegends: Record<string, unknown> = {};
    if (accountIds.length > 0) {
      entities[EntityType.COMPANY] = { $and: [{ id: { $in: accountIds } }], includeGroups: true };
      pinLegends[EntityType.COMPANY] = getColorShapeForEntity(
        mapViewState,
        colorPinLegends,
        shapePinLegends,
        EntityType.COMPANY
      );
    }
    if (contactIds.length > 0) {
      entities[EntityType.PERSON] = { $and: [{ id: { $in: contactIds } }], includeGroups: true };
      pinLegends[EntityType.PERSON] = getColorShapeForEntity(
        mapViewState,
        colorPinLegends,
        shapePinLegends,
        EntityType.PERSON
      );
    }

    const response: AggregatedListResponse<MapEntity, ListResponseAggregation<MapEntity>[]> =
      yield callApi("fetchPins", organizationId, {
        $filters: {
          cadence: true,
          entities,
          includeCustomFields: true,
          pinLegends,
        },
        $limit: 10000,
      });

    const accountPins = keyBy(
      response.data.filter((entity): entity is Company => entity.entity === EntityType.COMPANY),
      (entity) => entity.id
    ) as Record<Company["id"], Company & { shape?: MarkerShape }>;
    const contactPins = keyBy(
      response.data.filter((entity): entity is Person => entity.entity === EntityType.PERSON),
      (entity) => entity.id
    ) as Record<Person["id"], Person & { shape?: MarkerShape }>;

    yield put(fetchCheckInPins.success({ accountPins, contactPins }));
  } catch (error) {
    yield put(fetchCheckInPins.failure(error));
    yield put(handleError({ error }));
  }
}

function* onSetSelectedUserIds({ payload }: ReturnType<typeof setSelectedUserIds>) {
  if (payload.length === 0) {
    return;
  }

  // Enforce User Location visibility on selection (e.g. the ?previewUserLocations= deep link).
  const currentUser: undefined | User = yield select(getCurrentUser);
  const users: User[] = yield select(getUsers);
  const visibleUserIds = currentUser
    ? payload.filter((userId) =>
        users.some((user) => user.id === userId && isUserLocationVisibleToMe(currentUser, user))
      )
    : [];
  if (visibleUserIds.length !== payload.length) {
    yield put(setSelectedUserIds(visibleUserIds));
    return;
  }

  const currentDate: Date | undefined = yield select(getSelectedDate);
  if (currentDate === undefined) {
    const selectedUsers: User[] = yield select(getSelectedUsers);
    const orgTimeZone: string | undefined = yield select(
      getOrganizationSettingValue,
      OrganizationSetting.TIMEZONE
    );
    const initial = computeInitialSelectedDate(selectedUsers, orgTimeZone);
    yield put(setSelectedDate(initial));
    return;
  }

  const eventsRangeObj: DateRange | undefined = yield select(getSelectedDateRange);
  if (eventsRangeObj) {
    yield put(fetchEvents.request({ dateRange: toIsoDateRange(eventsRangeObj) }));
  }

  const widgetsData: undefined | WidgetsData = yield select(getWidgetsData);
  const loadedUserIds = new Set((widgetsData ?? []).map((item) => item.userId));
  const missingUserIds = payload.filter((userId) => !loadedUserIds.has(userId));
  if (missingUserIds.length > 0) {
    const period: UserLocationDatePeriod = yield select(getSelectedDatePeriod);
    const loadedWidgetsRange: IsoDateRange | undefined = yield select(getWidgetsDataDateRange);
    const widgetsRange =
      loadedWidgetsRange ??
      toIsoDateRange(getWidgetsDataDateRangeForSelection(period, currentDate));
    yield put(fetchWidgetsData.request({ dateRange: widgetsRange, userIds: missingUserIds }));
  }
}

function* onSelectionRangeChange() {
  const selectedUserIds: User["id"][] = yield select(getSelectedUserIds);
  if (selectedUserIds.length === 0) {
    return;
  }

  const eventsRangeObj: DateRange | undefined = yield select(getSelectedDateRange);
  if (!eventsRangeObj) {
    return;
  }

  const period: UserLocationDatePeriod = yield select(getSelectedDatePeriod);
  const date: Date | undefined = yield select(getSelectedDate);
  if (!date) {
    return;
  }

  const eventsRange = toIsoDateRange(eventsRangeObj);
  const loadedEventsRange: IsoDateRange | undefined = yield select(getEventsDateRange);
  if (!isEqual(eventsRange, loadedEventsRange)) {
    yield put(fetchEvents.request({ dateRange: eventsRange }));
  }

  const widgetsRange = toIsoDateRange(getWidgetsDataDateRangeForSelection(period, date));
  const loadedWidgetsRange: IsoDateRange | undefined = yield select(getWidgetsDataDateRange);
  if (!isEqual(widgetsRange, loadedWidgetsRange)) {
    yield put(invalidateWidgetsData());
    yield put(fetchWidgetsData.request({ dateRange: widgetsRange, userIds: selectedUserIds }));
  }
}

// Aggregate /distance server-side → { userId: { day: miles } }: SUM(totalDistance)
// grouped by user × calendar day, so only per-day totals come over the wire (no
// raw rows). `date` is a PG `date` column, so both the group-by and the range
// filter are zone-less: a calendar day has no time-of-day, and anchoring the range
// edges to a timezone would promote `date` to an instant and shift the window.
// Grouping by the raw `date` returns a clean "YYYY-MM-DD" bucket key. `totalDistance`
// is stored in meters server-side (PostGIS ST_Distance on geography points; same
// convention as the Mileage Tracking report's meterDistanceFormatter), so convert to miles.
function* fetchMilesByUserAndDay(
  organizationId: Organization["id"],
  userIds: User["id"][],
  startDate: string,
  endDate: string
) {
  const response: AggregatedListResponse<
    DistanceRecord,
    { grouped_data?: { field: string; sum?: number }[]; userId: User["id"] }[]
  > = yield callApi("fetchDistance", organizationId, {
    $aggs: {
      primaryGroup: { field: "userId" },
      secondaryGroup: { field: "date" },
      sum: ["totalDistance"],
    },
    $filters: {
      $and: [{ userId: { $in: userIds } }, { date: { $gte: startDate, $lte: endDate } }],
    },
    $limit: 0,
  });

  const milesByUserAndDay: Record<User["id"], Record<string, number>> = mapValues(
    keyBy(response.aggregations ?? [], (bucket) => bucket.userId),
    (bucket) =>
      mapValues(
        keyBy(bucket.grouped_data ?? [], (day) => day.field),
        (day) => (day.sum ?? 0) / METERS_IN_MILE
      )
  );
  return milesByUserAndDay;
}

// Aggregate /face-time server-side → { userId: { day: seconds } }: SUM(totalFaceTime)
// grouped by user × calendar day. The server-side SUM collapses the multiple rows
// per (userId, date) that exist due to crmActivityTypeId grouping. `date` is a PG
// `date` column, so both the group-by and the range filter are zone-less: anchoring
// the range edges to a timezone would promote `date` to an instant and shift the
// window. Grouping by the raw `date` returns a clean "YYYY-MM-DD" bucket key.
// `includeUnverified: true` makes the BE sum the stored totalFaceTime (verified +
// unverified check-in time), matching the Activities Logged tile, which counts all
// check-ins regardless of verification. Omitting it makes the BE rewrite the metric to
// the verified-only expression (FaceTimeService.customFetchRules), which zeroes
// unverified check-in durations.
function* fetchFaceTimeByUserAndDay(
  organizationId: Organization["id"],
  userIds: User["id"][],
  startDate: string,
  endDate: string
) {
  const response: AggregatedListResponse<
    FaceTime,
    { grouped_data?: { field: string; sum?: number }[]; userId: User["id"] }[]
  > = yield callApi("fetchFaceTime", organizationId, {
    $aggs: {
      primaryGroup: { field: "userId" },
      secondaryGroup: { field: "date" },
      sum: ["totalFaceTime"],
    },
    $filters: {
      $and: [{ userId: { $in: userIds } }, { date: { $gte: startDate, $lte: endDate } }],
      includeUnverified: true,
    },
    $limit: 0,
  });

  const faceTimeByUserAndDay: Record<User["id"], Record<string, number>> = mapValues(
    keyBy(response.aggregations ?? [], (bucket) => bucket.userId),
    (bucket) =>
      mapValues(
        keyBy(bucket.grouped_data ?? [], (day) => day.field),
        (day) => day.sum ?? 0
      )
  );
  return faceTimeByUserAndDay;
}

// Pivot /crmActivities aggregation → { userId: { day: checkInCount } }. Only check-ins are counted:
// `reliability` is non-null for check-ins (1 = verified, 0 = unverified) and null for other
// activities, so `reliability: { $ne: null }` keeps both verified and unverified check-ins. Anchored
// on completedAt (when the rep checked in) to match the dashboard Check-Ins card. Mobile blocks
// check-ins against records with no location, so reliability is always set for a real check-in — the
// filter does not undercount.
function* fetchActivitiesByUserAndDay(
  organizationId: Organization["id"],
  userIds: User["id"][],
  startDate: string,
  endDate: string,
  orgTimeZone: string
) {
  const response: AggregatedListResponse<
    DistanceRecord,
    {
      key: User["id"];
      secondary: { doc_count: number; key_as_string: string }[];
    }[]
  > = yield callApi("fetchActivities", organizationId, {
    $aggs: {
      primaryGroup: { field: "assignee.id" },
      secondaryGroup: {
        field: "completedAt",
        interval: "day",
        timezone: orgTimeZone,
      },
    },
    $filters: {
      $and: [
        {
          assigneeId: { $in: userIds },
          completedAt: { $gte: startDate, $lte: endDate, $timezone: orgTimeZone },
          reliability: { $ne: null },
        },
      ],
    },
    $limit: 0,
  });

  const activitiesByUserAndDay: Record<User["id"], Record<string, number>> = mapValues(
    keyBy(response.aggregations ?? [], (bucket) => bucket.key),
    (bucket) =>
      mapValues(
        keyBy(bucket.secondary, (day) =>
          formatISODate(parseApiDateWithTz(day.key_as_string, orgTimeZone))
        ),
        (day) => day.doc_count
      )
  );
  return activitiesByUserAndDay;
}

// Aggregate /deals server-side → { userId: { day: closedWonAmount } }: SUM(amount) of WON deals
// grouped by deal owner (userId) × closingDate-day. Unlike /distance and /face-time (PG `date`
// columns, zone-less), `closingDate` is a full datetime in ES, so it must be bucketed with
// interval:"day" + the org timezone — a plain-field group would bucket per instant. The range
// filter uses the same org timezone so the day buckets and the range window agree at the edges.
// Attribution is by owner, matching the dashboard Closed Won card / Reports revenue chart; amounts
// are summed raw with no currency conversion (org single-currency assumption, as everywhere else).
function* fetchClosedWonByUserAndDay(
  organizationId: Organization["id"],
  userIds: User["id"][],
  startDate: string,
  endDate: string,
  orgTimeZone: string
) {
  const response: AggregatedListResponse<
    Deal,
    { key: User["id"]; secondary: { key_as_string: string; sum_amount?: { value?: number } }[] }[]
  > = yield callApi("fetchDeals", organizationId, {
    $aggs: {
      primaryGroup: { field: "userId" },
      secondaryGroup: { field: "closingDate", interval: "day", timezone: orgTimeZone },
      sum: ["amount"],
    },
    $filters: {
      $and: [
        {
          closingDate: { $gte: startDate, $lte: endDate, $timezone: orgTimeZone },
          "stage.type": { $eq: DealStageType.WON },
          userId: { $in: userIds },
        },
      ],
    },
    $limit: 0,
  });

  const closedWonByUserAndDay: Record<User["id"], Record<string, number>> = mapValues(
    keyBy(response.aggregations ?? [], (bucket) => bucket.key),
    (bucket) =>
      mapValues(
        keyBy(bucket.secondary ?? [], (day) =>
          formatISODate(parseApiDateWithTz(day.key_as_string, orgTimeZone))
        ),
        (day) => day.sum_amount?.value ?? 0
      )
  );
  return closedWonByUserAndDay;
}

function* onFetchWidgetsData({
  payload: { dateRange, userIds },
}: ReturnType<typeof fetchWidgetsData.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const orgTimeZone: string = yield select(
      getOrganizationSettingValue,
      OrganizationSetting.TIMEZONE
    );

    const [startDate, endDate] = dateRange;

    const [
      milesByUserAndDay,
      faceTimeByUserAndDay,
      activitiesByUserAndDay,
      closedWonByUserAndDay,
    ]: [
      Record<User["id"], Record<string, number>>,
      Record<User["id"], Record<string, number>>,
      Record<User["id"], Record<string, number>>,
      Record<User["id"], Record<string, number>>,
    ] = yield all([
      call(fetchMilesByUserAndDay, organizationId, userIds, startDate, endDate),
      call(fetchFaceTimeByUserAndDay, organizationId, userIds, startDate, endDate),
      call(fetchActivitiesByUserAndDay, organizationId, userIds, startDate, endDate, orgTimeZone),
      call(fetchClosedWonByUserAndDay, organizationId, userIds, startDate, endDate, orgTimeZone),
    ]);

    const data: WidgetsData = userIds.flatMap((userId) => {
      const milesByDay = milesByUserAndDay[userId] ?? {};
      const faceTimeByDay = faceTimeByUserAndDay[userId] ?? {};
      const activitiesByDay = activitiesByUserAndDay[userId] ?? {};
      const closedWonByDay = closedWonByUserAndDay[userId] ?? {};
      const allDateKeys = union(
        Object.keys(milesByDay),
        Object.keys(faceTimeByDay),
        Object.keys(activitiesByDay),
        Object.keys(closedWonByDay)
      );
      return allDateKeys.map((dateKey) => ({
        activities: activitiesByDay[dateKey] ?? 0,
        closedWonAmount: closedWonByDay[dateKey] ?? 0,
        date: dateKey,
        faceTimeSeconds: faceTimeByDay[dateKey] ?? 0,
        miles: milesByDay[dateKey] ?? 0,
        userId,
      }));
    });

    yield put(fetchWidgetsData.success({ data }));
  } catch (error) {
    yield put(fetchWidgetsData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onInitUserLocations() {
  try {
    const metaData: Iam["metaData"] = yield select(getUserMetaData);
    yield put(
      initUserLocations.success({
        disabledUserIds: metaData.mapLegendUserLocationsDisabledUserIds ?? [],
      })
    );
  } catch (error) {
    yield put(initUserLocations.failure(error));
    yield put(handleError({ error }));
  }
}

function* onSetDisabledUserIds({ payload }: ReturnType<typeof setDisabledUserIds>) {
  yield put(updateMetadata.request({ mapLegendUserLocationsDisabledUserIds: payload }));
}

export function* onSetShowOtherPins({ payload }: ReturnType<typeof setShowOtherPins>) {
  yield call(localSettings.setUserLocationsShowOtherPins, payload);
}

function* onPersistUserLocationsSettings() {
  const { legendExpanded, visible }: BaseLayerData = yield select(getUserLocationsData);
  yield put(updateMetadata.request({ userLocationsSettings: { legendExpanded, visible } }));
}

const isToggleVisibilityOrLegendAction = isActionOf([
  toggleLayerVisibility,
  toggleLayerLegendExpandedState,
]);

export function* userLocationsSagas() {
  yield takeLatest(initUserLocations.request, onInitUserLocations);
  yield takeLatest(fetchEvents.request, onFetchEvents);
  yield takeLatest(fetchCheckInPins.request, onFetchCheckInPins);
  yield takeLatest(colorOrShapeKeyChanged, onColorOrShapeKeyChanged);
  yield takeLatest(setSelectedUserIds, onSetSelectedUserIds);
  yield takeLatest([setSelectedDate, setSelectedDatePeriod], onSelectionRangeChange);
  yield takeLatest(setDisabledUserIds, onSetDisabledUserIds);
  yield takeLatest(setShowOtherPins, onSetShowOtherPins);
  yield takeLatest(fetchWidgetsData.request, onFetchWidgetsData);
  yield takeLatest(
    (action: Action) =>
      isToggleVisibilityOrLegendAction(action) && action.payload.name === "userLocations",
    onPersistUserLocationsSettings
  );
}
